import React from "react";
import { Provider } from "react-redux";
import {
	BrowserRouter,
	Outlet,
	Route,
	Routes,
	useLocation,
	useNavigate
} from "react-router-dom";

import { ErrorBoundary } from "src/common/components/ErrorBoundary";
import { Content, Page } from "src/common/components/Page";
import {
	Authenticated,
	HasPermission,
	Permission,
	useAuthState
} from "src/features/auth";
import { Changelog, LastChangeModal } from "src/features/changelog";
import { ConfirmProvider } from "src/features/confirm";
import { FullStoryProvider } from "src/features/fullstory";
import { IntlProvider } from "src/features/intl";
import { NotificationList } from "src/features/notifications";
import { ThemeProvider } from "src/features/theme";
import { VersionCheck } from "src/features/version";
import { ModuleContent } from "src/modules";
import { store } from "src/store";
import { Dashboard } from "src/views/dashboard";
import { ForgottenPassword } from "src/views/forgotten-password";
import { FormExplorer } from "src/views/form-explorer";
import { FormRenderer } from "src/views/form-renderer";
import { Header } from "src/views/header";
import { InvitationList } from "src/views/invitation-list";
import { PasswordReset } from "src/views/password-reset";
import { AddOrEditPatient, EditPatient } from "src/views/patient-form";
import { PatientList } from "src/views/patient-list";
import { PatientTimeline } from "src/views/patient-timeline";
import { Report } from "src/views/report";
import { SendInvitation } from "src/views/send-invitation";
import { Sidebar } from "src/views/sidebar";
import { SignIn } from "src/views/sign-in";
import { SignOut } from "src/views/sign-out";
import { SignUp } from "src/views/sign-up";
import { Stats } from "src/views/stats";
import { StatusHistory } from "src/views/status-history";
import { UserList } from "src/views/user-list";
import { UserProfile } from "src/views/user-profile";

import { Information } from "./views/information";

const isUnderMaintenance = false;

export const App: React.FunctionComponent = () =>
	isUnderMaintenance ? <UnderMaintenance /> : <RegularOperation />;

// exported for eslint...
export const UnderMaintenance: React.FunctionComponent = () => (
	<div style={{ padding: 50, textAlign: "center" }}>
		<h1>Karbantartás alatt</h1>
		<p>Hamarosan visszatérünk!</p>
	</div>
);

// exported for eslint...
export const RegularOperation: React.FunctionComponent = () => (
	<AppWrapper>
		<AppContent />
	</AppWrapper>
);

const AppWrapper: React.FunctionComponent<React.PropsWithChildren> = ({
	children
}) => (
	<React.StrictMode>
		<ErrorBoundary>
			<Provider store={store}>
				<IntlProvider>
					<FullStoryProvider />
					<ThemeProvider>
						<ConfirmProvider>
							<BrowserRouter>{children}</BrowserRouter>
						</ConfirmProvider>
					</ThemeProvider>
				</IntlProvider>
			</Provider>
		</ErrorBoundary>
	</React.StrictMode>
);

const AppContent: React.FunctionComponent = () => (
	<>
		<div id="app">
			<Routes>
				<Route path="/sign-up" element={<SignUp />} />
				<Route path="/sign-in" element={<SignIn />} />
				<Route path="/sign-out" element={<SignOut />} />
				<Route
					path="/forgotten-password"
					element={<ForgottenPassword />}
				/>
				<Route path="/reset-password" element={<PasswordReset />} />
				<Route
					path="/*"
					element={
						<Authenticated>
							<RedirectToPatientList />
							<Routes>
								<Route path="/:org/:mod">
									<Route
										path="users"
										element={
											<Page>
												<Sidebar />
												<Content body={<Outlet />} />
											</Page>
										}
									>
										<Route
											path="list"
											element={
												<HasPermission
													permission={
														Permission.UsersRW
													}
													redirectTo="/"
												>
													<UserList />
												</HasPermission>
											}
										/>
										<Route
											path="profile"
											element={<UserProfile />}
										/>
									</Route>
									<Route path="patients">
										<Route
											path="list"
											element={
												<WithPatientsRW>
													<Page>
														<Sidebar />
														<Content
															body={
																<PatientList />
															}
														/>
													</Page>
												</WithPatientsRW>
											}
										/>
										<Route
											path="new"
											element={
												<WithPatientsRW>
													<Page>
														<Sidebar />
														<Content
															body={
																<AddOrEditPatient />
															}
														/>
													</Page>
												</WithPatientsRW>
											}
										/>
										<Route path=":patientId">
											<Route
												path="edit"
												element={
													<WithPatientsRW>
														<Page>
															<Sidebar />
															<ModuleContent
																alwaysShowScrollbar={
																	false
																}
																header={
																	<Header />
																}
																body={
																	<EditPatient />
																}
															/>
														</Page>
													</WithPatientsRW>
												}
											/>
											<Route
												path="dashboard"
												element={
													<WithPatientsRW>
														<Page>
															<Sidebar />
															<ModuleContent
																alwaysShowScrollbar={
																	true
																}
																header={
																	<Header />
																}
																body={
																	<Dashboard />
																}
															/>
															<FormExplorer />
														</Page>
													</WithPatientsRW>
												}
											/>
											<Route
												path="report"
												element={
													<WithPatientsRW>
														<Page>
															<Sidebar />
															<ModuleContent
																alwaysShowScrollbar={
																	true
																}
																header={
																	<Header />
																}
																body={
																	<Report />
																}
															/>
															<FormExplorer />
														</Page>
													</WithPatientsRW>
												}
											/>
											<Route
												path="status-history"
												element={
													<WithPatientsRW>
														<Page>
															<Sidebar />
															<ModuleContent
																alwaysShowScrollbar={
																	true
																}
																header={
																	<Header />
																}
																body={
																	<StatusHistory />
																}
															/>
															<FormExplorer />
														</Page>
													</WithPatientsRW>
												}
											/>
											<Route
												path="form/:formId"
												element={
													<WithPatientsRW>
														<Page>
															<Sidebar />
															<ModuleContent
																alwaysShowScrollbar={
																	true
																}
																header={
																	<Header />
																}
																body={
																	<FormRenderer />
																}
															/>
															<FormExplorer />
														</Page>
													</WithPatientsRW>
												}
											/>
											<Route
												path="form/:formId/:formVersion"
												element={
													<WithPatientsRW>
														<Page>
															<Sidebar />
															<ModuleContent
																alwaysShowScrollbar={
																	true
																}
																header={
																	<Header />
																}
																body={
																	<FormRenderer />
																}
															/>
															<FormExplorer />
														</Page>
													</WithPatientsRW>
												}
											/>
											<Route
												path="timeline"
												element={
													<WithTimeline>
														<Page>
															<Sidebar />
															<ModuleContent
																alwaysShowScrollbar={
																	true
																}
																header={
																	<Header />
																}
																body={
																	<PatientTimeline />
																}
															/>
															<FormExplorer />
														</Page>
													</WithTimeline>
												}
											/>
										</Route>
									</Route>
									<Route
										path="invitations"
										element={
											<HasPermission
												permission={Permission.UsersRW}
												redirectTo="/"
											>
												<Page>
													<Sidebar />
													<Content
														body={<Outlet />}
													/>
												</Page>
											</HasPermission>
										}
									>
										<Route
											path="list"
											element={<InvitationList />}
										/>
										<Route
											path="new"
											element={<SendInvitation />}
										/>
									</Route>
									<Route
										path="stats"
										element={
											<HasPermission
												permission={Permission.Stats}
												redirectTo="/"
											>
												<Page>
													<Sidebar />
													<Content body={<Stats />} />
												</Page>
											</HasPermission>
										}
									/>
									<Route
										path="information"
										element={
											<Page>
												<Sidebar />
												<Content
													body={<Information />}
												/>
											</Page>
										}
									/>
									<Route
										path="changelog"
										element={
											<Page>
												<Sidebar />
												<Content body={<Changelog />} />
											</Page>
										}
									/>
								</Route>
							</Routes>
							<VersionCheck />
							<LastChangeModal />
						</Authenticated>
					}
				/>
			</Routes>
			<NotificationList />
		</div>
		<div id="tooltip-root" />
	</>
);

const RedirectToPatientList: React.FunctionComponent = () => {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const { user } = useAuthState();
	React.useEffect(() => {
		if (pathname !== "/" || user === null) {
			return;
		}
		const { organization: org, module: mod } = user.permissions[0] || {};
		navigate(`/${org}/${mod}/patients/list`);
	}, [pathname, navigate, user]);
	return null;
};

const WithPatientsRW: React.FunctionComponent<React.PropsWithChildren> = ({
	children
}) => <>{children}</>;

const WithTimeline: React.FunctionComponent<React.PropsWithChildren> = ({
	children
}) => (
	<HasPermission permission={Permission.Timeline} redirectTo="/">
		{children}
	</HasPermission>
);
